// RecentProductsPanel.jsx (actualizado)
import {
  Box, Card, CardHeader, IconButton, Divider, List, ListItemButton,
  ListItemAvatar, Avatar, ListItemText, Chip, Stack, Tooltip, Typography, Skeleton, Pagination, TextField, InputAdornment
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import SearchIcon from "@mui/icons-material/Search";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { useEffect, useMemo, useState } from "react";
dayjs.extend(relativeTime);
dayjs.locale("es");

/** items: [{ id, code, description, createdAt|created_at }] */
export default function RecentProductsPanel({
  items = [],
  loading = false,
  supplierName,
  onSelect,
  onOpen,
  onClone,
  onRefresh,
  searchTerm = "",
  onSearchTermChange,
  searching = false,
  pageSize = 5,     // ← por defecto 5 por página
  sx                // ← estilos extra opcionales
}) {
  const [page, setPage] = useState(1);

  // Reinicia a la página 1 cuando cambien los datos o el tamaño de página
  useEffect(() => { setPage(1); }, [items, pageSize]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: { md: 400, lg: 440 },   // ← más ancho
        ml: { md: 3 },                 // ← más separación del form
        borderRadius: 3,
        overflow: "hidden",
        flexShrink: 1,
        minWidth: 0,
        border: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
        ...sx
      }}
      elevation={0}
    >
      <CardHeader
        title="Últimos productos creados"
        subheader={supplierName ? `Filtrados por: ${supplierName}` : "Selecciona un proveedor para filtrar"}
        action={
          <Tooltip title="Actualizar">
            <span>
              <IconButton onClick={() => onRefresh?.()} disabled={!onRefresh} size="small">
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
        sx={{
          "& .MuiCardHeader-title": { fontWeight: 700, fontSize: 16 },
          "& .MuiCardHeader-subheader": { fontSize: 12, opacity: 0.8 }
        }}
      />
      <Divider />

      <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          placeholder="Buscar histórico..."
          value={searchTerm}
          onChange={(e) => onSearchTermChange?.(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {(loading || searching) ? (
        <Box p={2}>
          {[...Array(pageSize)].map((_, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.25 }}>
              <Skeleton variant="circular" width={36} height={36} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="60%" />
              </Box>
              <Skeleton variant="rounded" width={64} height={22} />
            </Stack>
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Box p={3} textAlign="center">
          <Inventory2RoundedIcon />
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
            No hay productos recientes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cuando crees productos, aparecerán aquí.
          </Typography>
        </Box>
      ) : (
        <>
          <List dense disablePadding sx={{ py: 1 }}>
            {pageItems.map((p) => {
              const created = p.createdAt || p.created_at;
              const when = created ? dayjs(created).fromNow() : null;
              return (
                <ListItemButton
                  key={p.id}
                  onClick={
                    onClone
                      ? () => onClone(p)
                      : onSelect
                      ? () => onSelect(p)
                      : onOpen
                        ? () => onOpen(p)
                        : undefined
                  }
                  sx={(t) => ({
                    px: 1.25,
                    py: 1,
                    mx: 1,
                    mb: 0.75,
                    borderRadius: 2,
                    border: `1px solid ${alpha(t.palette.divider, 0.6)}`,
                    transition: "transform .12s ease",
                    "&:hover": {
                      bgcolor: alpha(t.palette.primary.main, 0.05),
                      borderColor: alpha(t.palette.primary.main, 0.35),
                      transform: "translateY(-1px)"
                    }
                  })}
                >
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      sx={(t) => ({
                        width: 36,
                        height: 36,
                        bgcolor: alpha(t.palette.primary.main, 0.12),
                        color: t.palette.primary.main
                      })}
                    >
                      <Inventory2RoundedIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Chip
                          size="small"
                          label={p.code || "—"}
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 700, height: 22 }}
                        />
                        {when && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {when}
                          </Typography>
                        )}
                      </Stack>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden"
                        }}
                        color="text.primary"
                        title={p.description}
                      >
                        {p.description}
                      </Typography>
                    }
                    primaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              );
            })}
          </List>

          {/* Paginación */}
          <Stack direction="row" justifyContent="center" sx={{ px: 2, pb: 2, pt: 0.5 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              size="small"
              siblingCount={0}
              boundaryCount={1}
            />
          </Stack>
        </>
      )}
    </Card>
  );
}


